import React, { FC, useRef, useEffect } from 'react'
import { useId } from '@reach/auto-id'
import { useRefWithUpdate } from 'hooks/useRefWithUpdate'
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import am4themes_animated from '@amcharts/amcharts4/themes/animated'
import { Series } from 'components/Charts/Series'

am4core.useTheme(am4themes_animated)

type DonutChartProps = {
  data: Series
  selected?: string[]
  onSelect?: (index: string[]) => void
}

export const DonutChart: FC<DonutChartProps> = ({ data, selected = [], onSelect }) => {
  const chartId = useId()
  const chartRef = useRef<am4charts.PieChart>()
  const filters = useRefWithUpdate<string[]>(selected)

  const handleSelection = (value: string) => {
    if (!onSelect) return
    const isSelected = filters.current.includes(value)

    const newSelected = isSelected
      ? filters.current.filter((filter) => value !== filter)
      : [...filters.current, value]

    const allSelected = newSelected.length === data.labels.length
    allSelected ? onSelect([]) : onSelect(newSelected)
  }

  const transformedData = data.labels.reduce((total: any[], label, index) => {
    //if (!data.values[0][index]) return total
    const obj: any = {
      label,
      color: am4core.color(data.colors[0][index]),
      values: data.values[0][index],
    }

    total.push(obj)
    return total
  }, [])

  useEffect(() => {
    const chart = am4core.create(chartId, am4charts.PieChart)
    chart.data = transformedData
    chart.width = am4core.percent(100)
    chart.height = am4core.percent(100)
    chart.align = 'center'
    chart.radius = am4core.percent(60)
    chart.innerRadius = am4core.percent(30)
    chart.paddingLeft = 0

    // Add and configure Series
    const pieSeries = chart.series.push(new am4charts.PieSeries())
    pieSeries.dataFields.value = 'values'
    pieSeries.dataFields.category = 'label'

    pieSeries.slices.template.propertyFields.fill = 'color'
    pieSeries.slices.template.stroke = am4core.color('#fff')
    pieSeries.slices.template.strokeWidth = 2
    pieSeries.slices.template.strokeOpacity = 0.6

    pieSeries.labels.template.wrap = true
    pieSeries.labels.template.maxWidth = 80
    pieSeries.labels.template.fontSize = 12

    // This creates initial animation
    pieSeries.hiddenState.properties.opacity = 0.6
    pieSeries.hiddenState.properties.endAngle = -90
    pieSeries.hiddenState.properties.startAngle = -90

    if (pieSeries.tooltip) {
      pieSeries.tooltip.getFillFromObject = false
      pieSeries.tooltip.background.fill = am4core.color('#FFF')
      pieSeries.tooltip.background.fillOpacity = 0.6
      pieSeries.tooltip.label.fill = am4core.color('#777')
    }

    pieSeries.slices.template.events.on(
      'hit',
      function (ev) {
        handleSelection((ev.target.dataItem as am4charts.PieSeriesDataItem)?.category || '')
      },
      chart,
    )

    pieSeries.events.on('ready', function (ev) {
      ev.target.slices.each((slice) => {
        slice.fillOpacity =
          slice.dataItem &&
            filters.current.includes((slice.dataItem as am4charts.PieSeriesDataItem).category)
            ? 0.3
            : 1
      })
    })

    // Remove default hover and active states
    const hoverState = pieSeries.slices.template.states.getKey('hover')
    hoverState!.properties.scale = 1

    const activeState = pieSeries.slices.template.states.getKey('active')
    activeState!.properties.shiftRadius = 0

    // Remove ugly amCharts icon xD (
    chart.events.on('validated', () => {

      chart.logo.visible = false;

    })

    chartRef.current = chart

    return () => {
      chartRef.current?.dispose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartId, `lenght-${data.labels.length}`])

  // Update selection
  useEffect(() => {
    if (!chartRef.current) return

    chartRef.current.series.each((serie) => {
      serie.slices.values.forEach((slice) => {
        const opacity =
          slice.dataItem &&
            selected.includes((slice.dataItem as am4charts.PieSeriesDataItem).category)
            ? 1
            : 0.6
        slice.fillOpacity = opacity
      })
    })
  }, [selected, chartRef])

  useEffect(() => {
    if (!chartRef.current) return

    transformedData.forEach((_i, index) => {
      const values = chartRef.current!.data[index]

      Object.keys(values).forEach((key: any) => {
        values[key] = transformedData[index][key]
      })
    })
    //[index]
    chartRef.current.invalidateRawData()
  }, [transformedData, selected])

  return <div id={chartId} style={{ width: '100%', height: '100%' }}></div>
}
